import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Dropdown from "../../../components/Atomos/DropDown/Dropdown";
import Input from "../../../components/Atomos/Inputs/Input";
import TextArea from "../../../components/Atomos/Inputs/TextArea";
import InputImage from "../../../components/Atomos/InputImage/InputImage";
import Plus from "../../../components/Atomos/Icons/EditMode/Plus";
import { PhoneNumber } from "../../../components/Atomos/PhoneNumber/PhoneNumber";
import Button from "../../../components/staff/Button";
import FormActionBar from "../../../components/staff/FormActionBar";
import { ServiceType } from "../../../Enums/FirmEnum";
import type { ServiceTypeEnum } from "../../../Enums/FirmEnum";
import { UseCountryList } from "../../../hooks/Catalogs";
import { UseRegionList } from "../../../hooks/Catalogs/useRegionListOptions";
import { useStatesList } from "../../../hooks/Catalogs/useStates";
import { useDynamicPageMeta } from "../../../hooks/useDynamicPageMeta";
import { useStaffMode } from "../../../hooks/StaffModeProvider";
import { getCitiesByState } from "../../../Services/City";
import { getAcceptedEmailDomainValidationError } from "../../../utils/emailValidation";
import { formatJurisdiction } from "../../../utils/jurisdiction/structuredJurisdiction";
import {
  buildCreateFirmJurisdiction,
  getServiceTypeOptions,
} from "./buildCreateFirmPayloads";
import {
  INITIAL_CREATE_FIRM_FORM,
  type CreateFirmFormState,
  type GeoSelection,
} from "./createFirmFormTypes";
import { useCreateFirmWithOffice } from "./useCreateFirmWithOffice";

const SECTION_TITLE_CLASS =
  "text-[#0070CD] font-decimal text-[26px] font-medium leading-[36px]";

export default function CreateFirmWithOffice(): React.ReactElement {
  const navigate = useNavigate();
  const { isStaffMode } = useStaffMode();
  const { setPageMeta, clearPageMeta } = useDynamicPageMeta();
  const { countryData } = UseCountryList();
  const { regionData } = UseRegionList();
  const { statesData } = useStatesList();

  const [form, setForm] = useState<CreateFirmFormState>(INITIAL_CREATE_FIRM_FORM);
  const [stateCities, setStateCities] = useState<
    { id: string; name: string }[]
  >([]);
  const [newEmailDomain, setNewEmailDomain] = useState("");
  const [newEmailDomainError, setNewEmailDomainError] = useState<string | undefined>();

  const { saving, fieldErrors, partialError, submit } = useCreateFirmWithOffice(
    regionData ?? [],
  );

  const serviceTypeOptions = useMemo(() => getServiceTypeOptions(), []);

  const selectedCountryRecord = useMemo(
    () => countryData.find((c: { id: string }) => c.id === form.country.id),
    [countryData, form.country.id],
  );

  useEffect(() => {
    setPageMeta({
      title: "Create Firm",
      breadcrumbLabels: {
        "/ManageFirm": "Manage Firm",
        "/ManageFirm/Create": "Create Firm",
      },
    });
    return () => clearPageMeta();
  }, [clearPageMeta, setPageMeta]);

  const countryStates = useMemo(
    () =>
      (statesData ?? []).filter(
        (s: { country?: string }) => s.country === form.country.id,
      ),
    [statesData, form.country.id],
  );

  useEffect(() => {
    if (!form.stateId) {
      setStateCities([]);
      return;
    }

    let cancelled = false;
    setStateCities([]);
    getCitiesByState(form.stateId)
      .then((data) => {
        if (!cancelled) {
          setStateCities(Array.isArray(data) ? data : []);
        }
      })
      .catch((error) => {
        console.error(error);
        if (!cancelled) {
          setStateCities([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [form.stateId]);

  const firmJurisdiction = useMemo(
    () => buildCreateFirmJurisdiction(form, "firm"),
    [form],
  );
  const officeJurisdiction = useMemo(
    () => buildCreateFirmJurisdiction(form, "office"),
    [form],
  );

  const isDirty = useMemo(() => {
    return (
      form.firmName.trim() !== "" ||
      form.displayName.trim() !== "" ||
      form.serviceType !== "" ||
      form.country.id !== "" ||
      form.state.trim() !== "" ||
      form.city.trim() !== "" ||
      form.address1.trim() !== "" ||
      form.address2.trim() !== "" ||
      form.zipCode.trim() !== "" ||
      form.logo != null ||
      form.phoneNumber.phone.trim() !== "" ||
      form.websiteUrl.trim() !== "" ||
      form.yearFounded.trim() !== "" ||
      form.totalProfessionals.trim() !== "" ||
      form.professionalsInPrimary.trim() !== "" ||
      form.acceptedEmailDomain.length > 0 ||
      form.firmOverview.trim() !== ""
    );
  }, [form]);

  const dirtyFieldLabels = useMemo(() => {
    const labels: string[] = [];
    if (form.firmName.trim()) {
      labels.push("Firm Name");
    }
    if (form.displayName.trim()) {
      labels.push("Short Name");
    }
    if (form.serviceType) {
      labels.push("Service Type");
    }
    if (form.country.id) {
      labels.push("Country");
    }
    if (form.state.trim()) {
      labels.push("State");
    }
    if (form.city.trim()) {
      labels.push("City");
    }
    if (form.address1.trim()) {
      labels.push("Address");
    }
    if (form.logo != null) {
      labels.push("Logo");
    }
    if (form.phoneNumber.phone.trim()) {
      labels.push("Phone Number");
    }
    if (form.websiteUrl.trim()) {
      labels.push("Website");
    }
    if (form.yearFounded.trim()) {
      labels.push("Year Founded");
    }
    if (form.totalProfessionals.trim()) {
      labels.push("Total Professionals");
    }
    if (form.professionalsInPrimary.trim()) {
      labels.push("Professionals in Primary");
    }
    if (form.acceptedEmailDomain.length) {
      labels.push("Accepted Email Domains");
    }
    if (form.firmOverview.trim()) {
      labels.push("Firm Overview");
    }
    return labels;
  }, [form]);

  const patchForm = (patch: Partial<CreateFirmFormState>) => {
    setForm((prev) => ({ ...prev, ...patch }));
  };

  const handleCountryChange = (country: GeoSelection & { regions?: { id: string }[] }) => {
    patchForm({
      country: { id: country.id, name: country.name },
      state: "",
      stateId: "",
      stateAbbreviation: "",
      city: "",
    });
  };

  const handleYearFoundedChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const value = event.target.value;
    if (value === "" || /^\d{0,4}$/.test(value)) {
      patchForm({ yearFounded: value });
    }
  };

  const handleProfessionalCountChange = (
    field: "totalProfessionals" | "professionalsInPrimary",
    event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const value = event.target.value;
    if (/^\d*$/.test(value)) {
      patchForm({ [field]: value });
    }
  };

  const handleAddEmailDomain = () => {
    const domain = newEmailDomain.trim();
    if (!domain) {
      return;
    }

    const validationError = getAcceptedEmailDomainValidationError(domain);
    if (validationError) {
      setNewEmailDomainError(validationError);
      return;
    }

    const normalizedDomain = domain.toLowerCase();
    if (
      form.acceptedEmailDomain.some(
        (existing) => existing.trim().toLowerCase() === normalizedDomain,
      )
    ) {
      setNewEmailDomainError("This domain has already been added.");
      return;
    }

    patchForm({
      acceptedEmailDomain: [...form.acceptedEmailDomain, normalizedDomain],
    });
    setNewEmailDomain("");
    setNewEmailDomainError(undefined);
  };

  const handleRemoveEmailDomain = (domain: string) => {
    patchForm({
      acceptedEmailDomain: form.acceptedEmailDomain.filter((item) => item !== domain),
    });
  };

  const handleSave = () => {
    void submit(form, selectedCountryRecord, {
      countryHasStates: countryStates.length > 0,
      stateHasCities: stateCities.length > 0,
    });
  };

  const goBack = useCallback(() => {
    navigate("/ManageFirm");
  }, [navigate]);

  return (
    <div className="flex min-w-0 flex-col bg-white">
      <div className="flex flex-col gap-6 p-6 pb-0">
        <div>
          <h1 className="text-2xl font-semibold text-gray-font">Create Firm</h1>
        </div>

        {partialError ? (
          <div
            className="rounded border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800"
            role="status"
          >
            {partialError}
          </div>
        ) : null}

        <section className="flex flex-col gap-5">
          <h2 className={SECTION_TITLE_CLASS}>Firm information</h2>
          <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
            <Input
              title="Firm Name"
              name="firmName"
              required
              value={form.firmName}
              onChange={(event) => patchForm({ firmName: event.target.value })}
              error={Boolean(fieldErrors.firmName)}
              errorMsj={fieldErrors.firmName}
              placeholder="Legal / official firm name"
            />
            <Input
              title="Short Name"
              name="displayName"
              value={form.displayName}
              onChange={(event) => patchForm({ displayName: event.target.value })}
              error={Boolean(fieldErrors.displayName)}
              errorMsj={fieldErrors.displayName}
              placeholder="Optional short name"
            />
            <Dropdown
              title="Service Type"
              required
              data={serviceTypeOptions}
              selectedObjst={
                form.serviceType
                  ? String(ServiceType[form.serviceType as ServiceTypeEnum])
                  : ""
              }
              seter={(option: { id: string }) =>
                patchForm({ serviceType: option.id as ServiceTypeEnum })
              }
              error={Boolean(fieldErrors.serviceType)}
              errormsj={fieldErrors.serviceType}
              getid
            />
            <Input
              title="Total Professionals"
              name="totalProfessionals"
              type="number"
              value={form.totalProfessionals}
              onChange={(event) =>
                handleProfessionalCountChange("totalProfessionals", event)
              }
              error={Boolean(fieldErrors.totalProfessionals)}
              errorMsj={fieldErrors.totalProfessionals}
              placeholder="Firm-wide total"
            />
            <Input
              title="Professionals in Primary"
              name="professionalsInPrimary"
              type="number"
              value={form.professionalsInPrimary}
              onChange={(event) =>
                handleProfessionalCountChange("professionalsInPrimary", event)
              }
              error={Boolean(fieldErrors.professionalsInPrimary)}
              errorMsj={fieldErrors.professionalsInPrimary}
              placeholder="Main office / jurisdiction size"
            />
          </div>
        </section>

        <section className="flex flex-col gap-5">
          <h2 className={SECTION_TITLE_CLASS}>Main office location</h2>
          <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
            <Dropdown
              title="Country"
              required
              data={countryData}
              selectedObjst={form.country.name}
              seter={handleCountryChange}
              error={Boolean(fieldErrors.country)}
              errormsj={fieldErrors.country}
              getid
            />
            {countryStates.length > 0 ? (
              <Dropdown
                title="State / Province"
                required
                data={countryStates}
                selectedObjst={form.state}
                seter={(option: { id: string; name: string; abbreviation?: string }) =>
                  patchForm({
                    state: option.name,
                    stateId: option.id,
                    stateAbbreviation: option.abbreviation ?? "",
                    city: "",
                  })
                }
                error={Boolean(fieldErrors.state)}
                errormsj={fieldErrors.state}
                getid
              />
            ) : null}
            {stateCities.length > 0 ? (
              <Dropdown
                title="City"
                required
                data={stateCities}
                selectedObjst={form.city}
                seter={(option: { name: string }) =>
                  patchForm({ city: option.name })
                }
                error={Boolean(fieldErrors.city)}
                errormsj={fieldErrors.city}
                getid
              />
            ) : null}
            <Input
              title="Address Line 1"
              name="address1"
              required
              value={form.address1}
              onChange={(event) => patchForm({ address1: event.target.value })}
              error={Boolean(fieldErrors.address1)}
              errorMsj={fieldErrors.address1}
            />
            <Input
              title="Address Line 2"
              name="address2"
              value={form.address2}
              onChange={(event) => patchForm({ address2: event.target.value })}
              error={false}
            />
            <Input
              title="Zip / Postal Code"
              name="zipCode"
              value={form.zipCode}
              onChange={(event) => patchForm({ zipCode: event.target.value })}
              error={false}
            />
            <Input
              title="Firm Jurisdiction"
              name="jurisdiction"
              disabled
              value={firmJurisdiction ? formatJurisdiction(firmJurisdiction) : ""}
              error={Boolean(fieldErrors.jurisdiction)}
              errorMsj={fieldErrors.jurisdiction}
            />
            <Input
              title="Office Jurisdiction"
              name="officeJurisdiction"
              disabled
              value={
                officeJurisdiction ? formatJurisdiction(officeJurisdiction) : ""
              }
              error={false}
            />
          </div>
        </section>

        <section className="flex flex-col gap-5">
          <h2 className={SECTION_TITLE_CLASS}>Firm additional information</h2>

          <InputImage
            required
            title="Logo"
            setImage={(logo) => patchForm({ logo })}
            photo={typeof form.logo === "string" ? form.logo : undefined}
            maxsize={25}
            dynamicSize
            adaptCropToImage
            maxPreviewWidth={240}
            maxPreviewHeight={240}
            error={Boolean(fieldErrors.logo)}
            errorMsg={fieldErrors.logo}
          />

          <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
            <PhoneNumber
              title="Phone Number"
              Obj={form.phoneNumber}
              seter={(phoneNumber) => patchForm({ phoneNumber })}
              error={Boolean(fieldErrors.phoneNumber)}
              errormsj={fieldErrors.phoneNumber}
            />
            <Input
              title="Website Url"
              name="websiteUrl"
              value={form.websiteUrl}
              onChange={(event) => patchForm({ websiteUrl: event.target.value })}
              error={Boolean(fieldErrors.websiteUrl)}
              errorMsj={fieldErrors.websiteUrl}
            />
            <Input
              title="Year Founded"
              name="yearFounded"
              value={form.yearFounded}
              onChange={handleYearFoundedChange}
              placeholder="YYYY"
              error={Boolean(fieldErrors.yearFounded)}
              errorMsj={fieldErrors.yearFounded}
            />
          </div>

          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-3 md:flex-row md:items-end">
              <div className="min-w-0 flex-1">
                <Input
                  title="Accepted Email Domain"
                  name="acceptedEmailDomain"
                  value={newEmailDomain}
                  onChange={(event) => {
                    setNewEmailDomain(event.target.value);
                    if (newEmailDomainError) {
                      setNewEmailDomainError(undefined);
                    }
                  }}
                  placeholder="e.g. acme.com or smithlaw.co.uk (without @)"
                  error={Boolean(
                    newEmailDomainError || fieldErrors.acceptedEmailDomain,
                  )}
                  errorMsj={
                    newEmailDomainError || fieldErrors.acceptedEmailDomain
                  }
                />
              </div>
              <div className="flex items-end md:shrink-0">
                <Button
                  text="Add"
                  color={isStaffMode ? "secondary" : "primary"}
                  variant="filled"
                  size="fixed"
                  icon={<Plus />}
                  onClick={handleAddEmailDomain}
                />
              </div>
            </div>
            {form.acceptedEmailDomain.map((domain) => (
              <div
                key={domain}
                className="flex flex-col gap-3 md:flex-row md:items-end"
              >
                <div className="min-w-0 flex-1">
                  <Input
                    title=""
                    name={`acceptedEmailDomain-${domain}`}
                    value={domain}
                    disabled
                    error={false}
                  />
                </div>
                <div className="flex items-end md:shrink-0">
                  <Button
                    text="Remove"
                    color="neutral"
                    variant="outlined"
                    size="fixed"
                    onClick={() => handleRemoveEmailDomain(domain)}
                  />
                </div>
              </div>
            ))}
          </div>

          <TextArea
            title="Firm Overview"
            name="firmOverview"
            required
            value={form.firmOverview}
            onChange={(event) => patchForm({ firmOverview: event.target.value })}
            maxChars={3500}
            characterCount
            height="min-h-[120px]"
            error={Boolean(fieldErrors.firmOverview)}
            errorMsj={fieldErrors.firmOverview}
          />
        </section>

      </div>
      <FormActionBar
        mode="add"
        isDirty={isDirty}
        dirtyFieldLabels={dirtyFieldLabels}
        onSave={handleSave}
        disabled={saving}
        showDeleteButton={false}
        onNavigateBack={goBack}
        className="!mb-0"
      />
    </div>
  );
}
